import React from 'react';
import { CareerLevel } from '../../types/career';

export type BadgeVariant =
  | 'emerald'
  | 'neon'
  | 'cyan'
  | 'amber'
  | 'rose'
  | 'slate'
  | 'purple'
  | 'level-1'
  | 'level-2'
  | 'level-3'
  | 'level-4'
  | 'level-5'
  | 'level-6'
  | 'level-7'
  | 'level-8';

export type BadgeSize = 'sm' | 'md' | 'lg';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  dot?: boolean;
  pulse?: boolean;
  level?: CareerLevel;
}

const variantStyles: Record<BadgeVariant, string> = {
  emerald: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  neon: 'bg-[#00E599]/15 text-[#00E599] border-[#00E599]/40 shadow-[0_0_10px_rgba(0,229,153,0.2)]',
  cyan: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
  amber: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  rose: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
  slate: 'bg-slate-700/40 text-slate-300 border-slate-600/40',
  purple: 'bg-purple-500/15 text-purple-400 border-purple-500/30',

  // Level-specific styles
  'level-1': 'bg-slate-700/50 text-slate-200 border-slate-500/40',
  'level-2': 'bg-sky-500/20 text-sky-300 border-sky-400/40',
  'level-3': 'bg-emerald-500/20 text-emerald-300 border-emerald-400/40',
  'level-4': 'bg-teal-500/20 text-teal-300 border-teal-400/40',
  'level-5': 'bg-blue-500/20 text-blue-300 border-blue-400/40',
  'level-6': 'bg-amber-500/25 text-amber-300 border-amber-400/50 shadow-[0_0_12px_rgba(245,158,11,0.25)]',
  'level-7': 'bg-purple-500/25 text-purple-300 border-purple-400/50 shadow-[0_0_12px_rgba(168,85,247,0.25)]',
  'level-8': 'bg-gradient-to-r from-amber-500/20 to-emerald-500/20 text-yellow-300 border-yellow-400/50 shadow-[0_0_15px_rgba(234,179,8,0.3)]',
};

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-[11px] leading-4 rounded-md gap-1',
  md: 'px-2.5 py-1 text-xs leading-4 rounded-lg gap-1.5',
  lg: 'px-3 py-1.5 text-sm leading-5 rounded-xl gap-2 font-medium',
};

export const Badge: React.FC<BadgeProps> = ({
  variant,
  level,
  size = 'md',
  dot = false,
  pulse = false,
  className = '',
  children,
  ...props
}) => {
  // If level is provided, map to level variant
  const effectiveVariant: BadgeVariant = level
    ? (`level-${level}` as BadgeVariant)
    : (variant || 'slate');

  return (
    <span
      className={`
        inline-flex items-center font-medium border select-none
        ${variantStyles[effectiveVariant]}
        ${sizeStyles[size]}
        ${className}
      `}
      {...props}
    >
      {dot && (
        <span className="relative flex h-2 w-2">
          {pulse && (
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75" />
          )}
          <span className="relative inline-flex rounded-full h-2 w-2 bg-current" />
        </span>
      )}
      {children}
    </span>
  );
};
